"use client";

import { useCallback } from "react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { createClient } from "@west-tennessee-consulting/ui";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Deal } from "@/lib/types/database/deals";

interface CEODashboardActionHandlers {
  sendEmailToDeal: (deal: Deal, contactEmails: string[]) => void;
  logActivityOnDeal: (deal: Deal, activityType: string, notes: string) => Promise<void>;
  assignDealOwners: (deal: Deal) => void;
  sendBulkEmail: (companies: any[]) => void;
}

export function useCeoDashboardActions(): CEODashboardActionHandlers {
  const { appUser } = useUniversalAuth();
  const { openModal } = useGlobalModalStack();
  const supabase = createClient();
  const queryClient = useQueryClient();

  const sendEmailToDeal = useCallback(
    (deal: Deal, contactEmails: string[]) => {
      if (!contactEmails || contactEmails.length === 0) {
        toast.error("This deal has no contact emails");
        return;
      }

      // Open compose modal with pre-filled recipients
      openModal("compose-message", {
        recipients: contactEmails,
        subject: `Follow-up: ${deal.title}`,
        context: {
          dealId: deal.id,
          dealTitle: deal.title,
          dealRevenue: deal.revenue,
        },
      });
    },
    [openModal]
  );

  const logActivityOnDeal = useCallback(
    async (deal: Deal, activityType: string, notes: string) => {
      try {
        const { error } = await supabase.from("activities").insert({
          entity_type: "deal",
          entity_id: deal.id,
          activity_type: activityType,
          notes,
          created_by: appUser?.id,
          created_at: new Date().toISOString(),
        });

        if (error) throw error;

        // Invalidate activity queries
        void queryClient.invalidateQueries({ queryKey: ["activities", deal.id] });

        toast.success("Activity logged successfully");
      } catch (error) {
        console.error("Error logging activity:", error);
        toast.error("Failed to log activity");
      }
    },
    [appUser?.id, supabase, queryClient]
  );

  const assignDealOwners = useCallback(
    (deal: Deal) => {
      // Open the deal owners assignment modal
      openModal("ceo-assign-owner", {
        deal,
      });
    },
    [openModal]
  );

  const sendBulkEmail = useCallback(
    (companies: any[]) => {
      if (!companies || companies.length === 0) {
        toast.error("No companies selected");
        return;
      }

      // Collect all company emails
      const emails = companies
        .map((c) => c.email)
        .filter((e) => e && e.length > 0);

      if (emails.length === 0) {
        toast.error("Selected companies have no email addresses");
        return;
      }

      // Open compose modal with bulk recipients
      openModal("compose-message", {
        recipients: emails,
        subject: "Important Update",
        context: {
          bulkEmail: true,
          companyCount: companies.length,
        },
      });
    },
    [openModal]
  );

  return {
    sendEmailToDeal,
    logActivityOnDeal,
    assignDealOwners,
    sendBulkEmail,
  };
}
